import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Easing } from "react-native";

export type PlayerPulseKind = "play" | "pause";

/** Owns the mature-style play/pause feedback pulse and rapid-input cancellation. */
export function usePlayerPulse(reduceMotion: boolean) {
  const [kind, setKind] = useState<PlayerPulseKind | null>(null);
  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.82)).current;
  const iconScale = useRef(new Animated.Value(0.88)).current;
  const animationRef = useRef<Animated.CompositeAnimation>();
  const generationRef = useRef(0);

  const show = useCallback((nextKind: PlayerPulseKind) => {
    generationRef.current += 1;
    const generation = generationRef.current;
    animationRef.current?.stop();
    setKind(nextKind);
    opacity.setValue(0);
    scale.setValue(0.72);
    iconScale.setValue(0.88);
    const duration = reduceMotion ? 0 : 680;
    const animation = Animated.parallel([
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: reduceMotion ? 0 : 95,
          useNativeDriver: true,
        }),
        Animated.delay(reduceMotion ? 0 : 340),
        Animated.timing(opacity, {
          toValue: 0,
          duration: reduceMotion ? 0 : 245,
          useNativeDriver: true,
        }),
      ]),
      Animated.timing(scale, {
        toValue: 1.16,
        duration,
        easing: Easing.bezier(0.2, 0.8, 0.2, 1),
        useNativeDriver: true,
      }),
      Animated.timing(iconScale, {
        toValue: 1.02,
        duration,
        easing: Easing.bezier(0.2, 0.8, 0.2, 1),
        useNativeDriver: true,
      }),
    ]);
    animationRef.current = animation;
    animation.start(({ finished }) => {
      if (finished && generationRef.current === generation) setKind(null);
    });
  }, [iconScale, opacity, reduceMotion, scale]);

  useEffect(() => () => animationRef.current?.stop(), []);

  return { kind, opacity, scale, iconScale, show };
}
